import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ArrowLeft, Edit, Save, X, User, Mail, Phone, MapPin, Briefcase, Calendar, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const TeamMemberProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [editMode, setEditMode] = useState(false);
  const [memberData, setMemberData] = useState({
    id: id,
    name: 'Dr. William Gray',
    email: 'wgray@stloralsurgery.com',
    phone: '(636) 555-0100',
    role: 'superadmin',
    position: 'Chief Oral Surgeon',
    department: 'Surgery',
    joinDate: '2018-01-15',
    address: '1635 Clarkson Rd, Chesterfield, MO 63017',
    bio: 'Board-certified oral and maxillofacial surgeon with over 15 years of experience. Specializes in complex implant cases and full-arch restorations.',
    specialties: ['Dental Implants', 'Wisdom Teeth', 'Bone Grafting', 'Full Arch Restoration'],
    education: 'DDS - Washington University School of Dental Medicine',
    status: 'active'
  });

  const stats = {
    referralsHandled: 847,
    patientsServed: 1243,
    satisfactionRate: 98,
    activeDeals: 12
  };

  const handleSave = () => {
    console.log('Saving member data:', memberData);
    alert('Team member profile updated successfully!');
    setEditMode(false);
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'bg-green-100 text-green-800 border-green-200' : 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      superadmin: 'bg-purple-100 text-purple-800 border-purple-200',
      admin: 'bg-blue-100 text-blue-800 border-blue-200',
      staff: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <Button variant="ghost" onClick={() => navigate('/team')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Team
        </Button>
        {!editMode ? (
          <Button
            className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
            onClick={() => setEditMode(true)}
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit Profile
          </Button>
        ) : (
          <div className="flex space-x-2">
            <Button
              className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
              onClick={handleSave}
            >
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
            <Button variant="outline" onClick={() => setEditMode(false)}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Profile */}
        <div className="lg:col-span-2 space-y-6">
          {/* Header Card */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start space-x-6">
                <Avatar className="h-24 w-24 border-4 border-[#D4AF37]">
                  <AvatarFallback className="bg-[#0A1628] text-[#D4AF37] text-2xl font-bold">
                    {memberData.name.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h2 className="text-2xl font-bold text-[#0A1628]">{memberData.name}</h2>
                    <Badge className={getRoleBadgeColor(memberData.role)}>
                      {memberData.role}
                    </Badge>
                    <Badge className={getStatusColor(memberData.status)}>
                      {memberData.status}
                    </Badge>
                  </div>
                  <p className="text-lg text-gray-700 mb-1">{memberData.position}</p>
                  <p className="text-sm text-gray-600">{memberData.department} Department</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <User className="h-5 w-5 mr-2 text-[#D4AF37]" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center">
                    <Mail className="h-4 w-4 mr-1" />
                    Email
                  </label>
                  {editMode ? (
                    <Input
                      value={memberData.email}
                      onChange={(e) => setMemberData({...memberData, email: e.target.value})}
                    />
                  ) : (
                    <p className="text-gray-700">{memberData.email}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center">
                    <Phone className="h-4 w-4 mr-1" />
                    Phone
                  </label>
                  {editMode ? (
                    <Input
                      value={memberData.phone}
                      onChange={(e) => setMemberData({...memberData, phone: e.target.value})}
                    />
                  ) : (
                    <p className="text-gray-700">{memberData.phone}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  Office Address
                </label>
                {editMode ? (
                  <Input
                    value={memberData.address}
                    onChange={(e) => setMemberData({...memberData, address: e.target.value})}
                  />
                ) : (
                  <p className="text-gray-700">{memberData.address}</p>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center">
                    <Briefcase className="h-4 w-4 mr-1" />
                    Position
                  </label>
                  {editMode ? (
                    <Input
                      value={memberData.position}
                      onChange={(e) => setMemberData({...memberData, position: e.target.value})}
                    />
                  ) : (
                    <p className="text-gray-700">{memberData.position}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Department</label>
                  {editMode ? (
                    <Input
                      value={memberData.department}
                      onChange={(e) => setMemberData({...memberData, department: e.target.value})}
                    />
                  ) : (
                    <p className="text-gray-700">{memberData.department}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Professional Information */}
          <Card>
            <CardHeader>
              <CardTitle>Professional Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Bio</label>
                {editMode ? (
                  <Textarea
                    value={memberData.bio}
                    onChange={(e) => setMemberData({...memberData, bio: e.target.value})}
                    rows={4}
                  />
                ) : (
                  <p className="text-gray-700">{memberData.bio}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Education</label>
                {editMode ? (
                  <Input
                    value={memberData.education}
                    onChange={(e) => setMemberData({...memberData, education: e.target.value})}
                  />
                ) : (
                  <p className="text-gray-700">{memberData.education}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Specialties</label>
                <div className="flex flex-wrap gap-2">
                  {memberData.specialties.map((specialty, index) => (
                    <Badge key={index} variant="outline">{specialty}</Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Performance Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Performance Stats</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Referrals Handled</span>
                <span className="font-semibold text-[#0A1628]">{stats.referralsHandled}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Patients Served</span>
                <span className="font-semibold text-[#D4AF37]">{stats.patientsServed}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Satisfaction Rate</span>
                <span className="font-semibold text-green-600">{stats.satisfactionRate}%</span>
              </div>
              <div className="flex items-center justify-between pt-3 border-t">
                <span className="text-sm text-gray-600">Active Deal Rooms</span>
                <span className="font-semibold text-blue-600">{stats.activeDeals}</span>
              </div>
            </CardContent>
          </Card>

          {/* Account Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <Shield className="h-5 w-5 mr-2 text-[#D4AF37]" />
                Account Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Member Since</span>
                <span className="font-semibold text-[#0A1628]">
                  {new Date(memberData.joinDate).toLocaleDateString()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Account Type</span>
                <Badge className={getRoleBadgeColor(memberData.role)}>
                  {memberData.role}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Status</span>
                <Badge className={getStatusColor(memberData.status)}>
                  {memberData.status}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="bg-blue-50 border-blue-200">
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button className="w-full" variant="outline">
                Send Message
              </Button>
              <Button className="w-full" variant="outline">
                View Schedule
              </Button>
              <Button className="w-full" variant="outline">
                Performance Report
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TeamMemberProfile;